// AttendeeList.tsx
/* eslint-disable */
import React, { useState } from 'react';
import { Registration, UpdateRegistrationInput, RegistrationStatusEnum } from './API';
import { updateRegistrationDetails } from './mutations/processUpdateRegistration';
import DownloadModal from './DownloadModal';
import Switch from "react-switch";
import { Select, Input, Checkbox, SearchIcon } from '@uclaea/react-component-library';
import './css/AttendeeList.css';
import useDebouncedValue from './hooks/useDebouncedValue';
import AttendeeRow from './components/AttendeeRow';
import BulkActions from './components/BulkActions';
import PaginationControls from './components/PaginationControls';
import { formatDate } from './utils/formatDate';
import { parseAdditionalData, serializeAdditionalData } from './utils/parseAdditionalData';
import { calculateCheckInStatus } from './utils/registrationStatus';
import { generateAttendeeCsv, downloadCsv } from './utils/csv';

const ROWS_PER_PAGE = 25;

interface AttendeeListProps {
  attendees: Registration[];
  setAttendees: React.Dispatch<React.SetStateAction<Registration[]>>;
}

function AttendeeList(props: AttendeeListProps) {
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedQuery = useDebouncedValue(searchQuery, 300);
  const [testToggled, setTestToggled] = useState<boolean>(false);
  const [invitedToggled, setInvitedToggled] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [selectedAttendees, setSelectedAttendees] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<string>('');
  const [rowActions, setRowActions] = useState<Record<string, number>>({});
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [selectedDownloadFormat, setSelectedDownloadFormat] = useState<string>('csv');
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Filter, sort, and map attendees
  const filteredAttendees = [...props.attendees]
    .map(attendee => {
      const parsed = parseAdditionalData(attendee.additionalData);
      return {
        ...attendee,
        membership: parsed.membership || '',
        guests: attendee.guests || 0,
        checkedInCount: attendee.checkedInCount ?? 0,
        notes: parsed.notes || '',
      };
    })
    .sort((a, b) => {
      if (sortConfig !== null) {
        const { key, direction } = sortConfig;
        let aValue = a[key];
        let bValue = b[key];
        // Special handling for 'notes' column
        if (key === 'notes') {
          aValue = (a.notes ?? '').toLowerCase();
          bValue = (b.notes ?? '').toLowerCase();
        } else {
          if (typeof aValue === 'string') aValue = aValue.toLowerCase();
          if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        }
        if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
        return 0;
      }
      return 0;
    })
    .filter(attendee => {
      const query = debouncedQuery.toLowerCase();
      const firstName = attendee.firstName ? attendee.firstName.toLowerCase() : '';
      const lastName = attendee.lastName ? attendee.lastName.toLowerCase() : '';
      const email = attendee.email ? attendee.email.toLowerCase() : '';
      const fullName = `${firstName} ${lastName}`;
      const matchesQuery = fullName.includes(query) || email.includes(query);
      const showTestRegistrations = testToggled || attendee.live;
      const showInvitedRegistrations = invitedToggled || attendee.registrationStatus !== RegistrationStatusEnum.INVITED;
      const matchesStatus = statusFilter === 'ALL' || attendee.registrationStatus === statusFilter;
      return matchesQuery && showTestRegistrations && showInvitedRegistrations && matchesStatus;
    });

  // Pagination
  const paginatedAttendees = filteredAttendees.slice(currentPage * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const totalAttendees = filteredAttendees.length;
  const totalPages = Math.ceil(totalAttendees / ROWS_PER_PAGE);

  // Handlers
  const handleSubmit = async (newCheckedInCount: number, attendee: Registration) => {
    const registrationData: UpdateRegistrationInput = {
      eventId: attendee.eventId,
      registrationId: attendee.registrationId,
      checkedInCount: newCheckedInCount,
      registrationStatus: calculateCheckInStatus(newCheckedInCount, attendee.guests ?? 0)
    };
    try {
      const response = await updateRegistrationDetails(registrationData);
      if (response && response.checkedInCount === newCheckedInCount) {
        props.setAttendees(prevAttendees =>
          prevAttendees.map(a =>
            a.registrationId === attendee.registrationId
              ? { ...a, checkedInCount: newCheckedInCount, registrationStatus: registrationData.registrationStatus }
              : a
          )
        );
        setRowActions(prevRowActions => ({ ...prevRowActions, [attendee.registrationId]: 0 }));
      } else {
        console.error('Unexpected response structure or status mismatch:', response);
      }
      setActiveDropdownId(null);
    } catch (err) {
      console.error('Error updating registration:', err);
      setActiveDropdownId(null);
    }
  };

  const handleTestToggleChange = (checked: boolean) => setTestToggled(checked);
  const handleInvitedToggleChange = (checked: boolean) => setInvitedToggled(checked);
  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredAttendees.map(attendee => attendee.registrationId);
      setSelectedAttendees(new Set(allIds));
    } else {
      setSelectedAttendees(new Set());
    }
  };
  const toggleSelectOne = (id: string) => {
    const newSet = new Set(selectedAttendees);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedAttendees(newSet);
  };

  const handleBulkSubmit = async () => {
    const attendeesArray = Array.from(selectedAttendees);
    if (bulkAction === "CHECK_IN") {
      const confirmCheckIn = window.confirm(`You are about to check in ${attendeesArray.length} attendee(s). Confirm?`);
      if (!confirmCheckIn) return;
      const promises = attendeesArray.map(async (id) => {
        const attendee = props.attendees.find(a => a.registrationId === id);
        if (attendee) {
          const guests = attendee.guests && attendee.guests > 0 ? attendee.guests : 1;
          return handleSubmit(guests, attendee);
        }
      });
      await Promise.all(promises);
    } else if (bulkAction === "UNDO_CHECK_IN") {
      const confirmUndoCheckIn = window.confirm(`You are about to undo check-in for ${attendeesArray.length} attendee(s). Confirm?`);
      if (!confirmUndoCheckIn) return;
      const promises = attendeesArray.map(async (id) => {
        const attendee = props.attendees.find(a => a.registrationId === id);
        if (attendee) {
          return handleSubmit(0, attendee);
        }
      });
      await Promise.all(promises);
    } else if (bulkAction === "SUBMIT_DATA") {
      alert("Data upload successful. Selected attendees have been marked to be submitted to CRM by end of day.");
      props.setAttendees(prevAttendees =>
        prevAttendees.map(attendee => {
          if (selectedAttendees.has(attendee.registrationId)) {
            return { ...attendee, submittedToCRM: true };
          }
          return attendee;
        })
      );
    } else if (bulkAction === "DOWNLOAD") {
      setShowDownloadModal(true);
      return;
    }
  };

  const handleDownloadConfirm = () => {
    if (selectedDownloadFormat === 'csv') {
      const selectedData = props.attendees.filter(att => selectedAttendees.has(att.registrationId));
      if (selectedData.length === 0) {
        alert("No attendees selected for download.");
        setShowDownloadModal(false);
        return;
      }
      const csvContent = generateAttendeeCsv(selectedData);
      downloadCsv(csvContent, 'attendees.csv');
    } else if (selectedDownloadFormat === 'excel') {
      alert("Excel download is not yet implemented.");
    } else {
      alert("Invalid download format specified.");
    }
    setShowDownloadModal(false);
  };

  const handleSort = (columnKey: string) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const handleNextPage = () => setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  const handlePreviousPage = () => setCurrentPage((prevPage) => Math.max(prevPage - 1, 0));

  // Handler to save both check-in count and notes for a specific attendee
  const handleSaveRow = async (attendee: Registration & { notes?: string; membership?: string }) => {
    const additionalDataObj = parseAdditionalData(attendee.additionalData);
    const { membership, notes: attendeeNotes } = attendee;
    additionalDataObj.notes = notes[attendee.registrationId] !== undefined ? notes[attendee.registrationId] : attendeeNotes || '';
    if (membership) {
      additionalDataObj.membership = membership;
    }
    const newAdditionalData = serializeAdditionalData(additionalDataObj);
    const newCheckedInCount = rowActions[attendee.registrationId] ?? attendee.checkedInCount;
    const registrationData: UpdateRegistrationInput = {
      eventId: attendee.eventId,
      registrationId: attendee.registrationId,
      checkedInCount: newCheckedInCount,
      registrationStatus: calculateCheckInStatus(newCheckedInCount, attendee.guests ?? 0),
      additionalData: newAdditionalData,
    };
    try {
      await updateRegistrationDetails(registrationData);
      props.setAttendees(prevAttendees =>
        prevAttendees.map(a =>
          a.registrationId === attendee.registrationId
            ? { ...a, checkedInCount: newCheckedInCount, registrationStatus: registrationData.registrationStatus, additionalData: newAdditionalData }
            : a
        )
      );
      setRowActions(prevRowActions => ({ ...prevRowActions, [attendee.registrationId]: 0 }));
    } catch (err) {
      console.error('Error updating registration:', err);
    }
  };

  return (
    <>
      <div className='registrations-options-container'>
        <h2 className="registrations-options-header">Registrants</h2>
        <div className='registrations-status-container'>
          <p className='registrations-status-header-text'>Status:</p>
          <p className='registrations-status-value-text'>
            {props.attendees.filter((reg) => reg.registrationStatus === "CHECKED_IN" || reg.registrationStatus === "PARTIAL_CHECK_IN").length}/{props.attendees.length}
          </p>
        </div>
      </div>
      <div className='attendee-list-container'>
        <div className="toggle-row">
          <h4 className="attendee-header">Filter by Status</h4>
          <div className="filter-status-select">
            <Select
              label=""
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { label: 'All', id: 'all', value: 'ALL' },
                { label: 'Registered', id: 'registered', value: 'REGISTERED' },
                { label: 'Checked In', id: 'checked-in', value: 'CHECKED_IN' },
                { label: 'Partial Check-In', id: 'partial', value: 'PARTIAL_CHECK_IN' },
                { label: 'Cancelled', id: 'cancelled', value: 'CANCELLED' },
                { label: 'Invited', id: 'invited', value: 'INVITED' },
              ]}
            />
          </div>
        </div>
        <div className="toggle-row">
          <h4 className="attendee-header">Show Test Registrations</h4>
          <Switch onChange={handleTestToggleChange} checked={testToggled} />
        </div>
        <Input
          label="Search"
          type="text"
          placeholder="Search by name or email..."
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={{ left: SearchIcon }}
        />
        <BulkActions bulkAction={bulkAction} setBulkAction={setBulkAction} onSubmit={handleBulkSubmit} />
        <table className="attendee-table">
          <thead>
            <tr>
              <th>
                <Checkbox
                  label=""
                  onChange={e => toggleSelectAll((e.target as HTMLInputElement).checked)}
                  checked={selectedAttendees.size === filteredAttendees.length}
                  aria-label="Select all attendees"
                />
              </th>
              <th onClick={() => handleSort('lastName')}>Name</th>
              <th onClick={() => handleSort('email')}>Email</th>
              <th onClick={() => handleSort('dateCreated')} className='hide-for-mobile'>Transaction date</th>
              <th>Status</th>
              <th onClick={() => handleSort('guests')} className='hide-for-mobile'>Guests</th>
              <th onClick={() => handleSort('notes')} className='hide-for-mobile'>Notes</th>
              <th>Check In</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAttendees.map(attendee => (
              <AttendeeRow
                key={attendee.registrationId}
                attendee={attendee}
                selected={selectedAttendees.has(attendee.registrationId)}
                onSelect={() => toggleSelectOne(attendee.registrationId)}
                notesValue={notes[attendee.registrationId] !== undefined ? notes[attendee.registrationId] : attendee.notes || ''}
                onNotesChange={val => setNotes(prev => ({ ...prev, [attendee.registrationId]: val }))}
                onSave={() => handleSaveRow(attendee)}
                rowAction={rowActions[attendee.registrationId] ?? attendee.checkedInCount}
                onRowActionChange={val => setRowActions({ ...rowActions, [attendee.registrationId]: val })}
                disabled={attendee.registrationStatus === 'CANCELLED'}
                formatDate={formatDate}
                activeDropdownId={activeDropdownId}
              />
            ))}
          </tbody>
        </table>
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={handlePreviousPage}
          onNext={handleNextPage}
        />
      </div>
      {showDownloadModal && (
        <DownloadModal
          selectedDownloadFormat={selectedDownloadFormat}
          setSelectedDownloadFormat={setSelectedDownloadFormat}
          onConfirm={handleDownloadConfirm}
          onCancel={() => setShowDownloadModal(false)}
        />
      )}
    </>
  );
}

export default AttendeeList;